import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaLightbulb,
  FaStar,
  FaUsers,
  FaScaleBalanced,
  FaArrowRight,
} from "react-icons/fa6";
import PageHero from "../components/PageHero";

export default function About() {
  useEffect(() => {
    document.title = 'About — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="About the Academy"
        title={
          <>
            A forward-thinking platform for{" "}
            <span className="italic text-brand-deep">education,</span>{" "}
            <span className="italic text-brand-aubergine">research,</span>{" "}
            and{" "}
            <span className="italic text-brand-moss">institutional impact.</span>
          </>
        }
        lead="Learning Outcomes Academy was founded to empower learners and organizations through specialized programs that foster innovation, critical thinking, and professional growth — bridging education, research, technology, and institutional development."
      />

      {/* Positioning grid */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow eyebrow-bar">Positioning</div>
            <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
              Built to help learners and organizations move from knowledge to measurable impact.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              { t: "Tailored learning", d: "Specialized courses and workshops for parents, students, educators, researchers, and professionals." },
              { t: "Research development", d: "Practical guidance from study design through publication and peer review." },
              { t: "Accreditation support", d: "Structured preparation, self-study reports, and visit-ready evidence organization." },
              { t: "Future-ready tools", d: "Cloud-based tools in development for data analysis and accreditation reporting." },
            ].map((b) => (
              <div key={b.t}>
                <div className="h-px w-10 bg-brand-deep mb-4" />
                <h3 className="font-display text-xl text-ink">{b.t}</h3>
                <p className="mt-2 text-slate2 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-cream">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <article className="bg-white rounded-2xl p-8 border border-ink/5 shadow-soft relative overflow-hidden">
              <span className="absolute -top-6 -right-6 font-display text-[120px] leading-none text-brand-sage/40 select-none">
                M
              </span>
              <div className="eyebrow">Mission</div>
              <p className="mt-3 font-display text-2xl leading-snug text-ink">
                To provide top-tier educational resources, expert guidance, and practical
                tools that equip individuals and institutions with the skills and
                confidence needed for academic and professional success.
              </p>
              <p className="mt-4 text-slate2 leading-relaxed">
                We believe in fostering a learning environment that is engaging,
                accessible, innovative, and impactful.
              </p>
            </article>
            <article className="bg-ink text-bone rounded-2xl p-8 relative overflow-hidden">
              <span className="absolute -top-6 -right-6 font-display text-[120px] leading-none text-brand-aubergine/50 select-none">
                V
              </span>
              <div className="eyebrow !text-brand-sage">
                <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
                Vision
              </div>
              <p className="mt-3 font-display text-2xl leading-snug">
                To become a leading provider of specialized education — helping learners,
                educators, researchers, professionals, institutions, and the public excel
                in research, program development, accreditation, and lifelong learning.
              </p>
            </article>
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/facilitators"
              className="inline-flex items-center gap-2 text-brand-deep font-medium hover:underline"
            >
              Curious who teaches our courses? Meet our facilitators{" "}
              <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="eyebrow eyebrow-bar">Our Values</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                The commitments that shape how we work with every learner and partner.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <FaLightbulb />, title: "Innovation", text: "Embracing new ideas, methods, and tools to move learning forward." },
              { icon: <FaStar />, title: "Excellence", text: "Delivering high-quality education and services that meet real needs." },
              { icon: <FaUsers />, title: "Collaboration", text: "Working with people and institutions toward shared, measurable goals." },
              { icon: <FaScaleBalanced />, title: "Integrity", text: "Upholding the highest standards of honesty and professionalism." },
            ].map((v) => (
              <div
                key={v.title}
                className="bg-white rounded-2xl border border-ink/5 p-6 shadow-soft"
              >
                <span className="text-brand-deep text-xl">{v.icon}</span>
                <h3 className="mt-5 font-display text-xl text-ink">{v.title}</h3>
                <p className="mt-2 text-slate2 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bone">
        <div className="container-page pb-20 pt-16">
          <div className="rounded-3xl bg-ink text-bone p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h3 className="font-display text-2xl lg:text-3xl leading-tight">
                Looking to bring our work into your institution or team?
              </h3>
              <p className="mt-3 text-bone/70 leading-relaxed">
                We partner with departments, programs, and organizations on tailored
                engagements across courses, research, accreditation, and program
                development.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-cream transition-colors"
              >
                Start a conversation <FaArrowRight className="text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
